import type { OnboardingTask, OnboardingTaskId } from './types'

/**
 * Centralized task registry - all onboarding tasks defined in one place
 * This makes it easy to add, modify, or remove tasks without touching multiple files
 * Uses the same structure as the existing config.ts file
 */
export const ONBOARDING_TASKS: Record<OnboardingTaskId, OnboardingTask> = {
	setupIntegration: {
		order: 1,
		id: 'setupIntegration',
		name: 'Setup Integration',
		description:
			'Connect BitLyft AIR® to your environment to unlock its full capabilities.',
		completed: false,
		required: true,
		roleRequired: 'Read Only',
		dependsOn: null,
		completedAt: null,
		skipped: false,
		skippedAt: null,
		link: '/integrations',
		docs: 'https://bitlyft.atlassian.net/wiki/spaces/BAHD/pages/886046727/How+to+Integrate+M365+in+BitLyft+AIR',
	},

	runAction: {
		order: 2,
		id: 'runAction',
		name: 'Run an Action',
		description:
			'Test out individual actions in BitLyft AIR® to see how you can automate tasks or respond to specific security events in real time.',
		completed: false,
		required: false,
		roleRequired: 'Analyst',
		dependsOn: ['setupIntegration'],
		completedAt: null,
		skipped: false,
		skippedAt: null,
		link: '/actions',
		docs: 'https://bitlyft.atlassian.net/wiki/spaces/BAHD/pages/1067614217/How+to+Manually+Run+Automated+Actions+Playbooks#How-to-Run-a-Remediation-Action',
	},

	runPlaybook: {
		order: 3,
		id: 'runPlaybook',
		name: 'Run a Playbook',
		description:
			'Execute a complete playbook to experience how BitLyft AIR® chains together multiple actions to handle complex incident response or automation scenarios.',
		completed: false,
		required: false,
		roleRequired: 'Analyst',
		dependsOn: ['setupIntegration'],
		completedAt: null,
		skipped: false,
		skippedAt: null,
		link: '/playbooks',
		docs: 'https://bitlyft.atlassian.net/wiki/spaces/BAHD/pages/1067614217/How+to+Manually+Run+Automated+Actions+Playbooks#How-to-Run-a-Playbook',
	},

	reviewPolicies: {
		order: 4,
		id: 'reviewPolicies',
		name: 'Review BitLyft AIR® Policies',
		description:
			'Access the Policies section to view and manage detection rules, correlation logic, and security policies that drive threat detection and automated responses in BitLyft AIR®.',
		completed: false,
		required: false,
		roleRequired: 'Read Only',
		dependsOn: null,
		completedAt: null,
		skipped: false,
		skippedAt: null,
		link: '/air/policy-management',
		docs: 'https://bitlyft.atlassian.net/wiki/spaces/BAHD/pages/1219264518/Policy+Management',
	},

	mapAutomation: {
		order: 5,
		id: 'mapAutomation',
		name: 'Map an Automation',
		description:
			'Use the intuitive UI to map triggers, conditions, and actions to build custom automated workflows that fit your unique security processes.',
		completed: false,
		required: false,
		roleRequired: 'Analyst',
		dependsOn: ['setupIntegration'],
		completedAt: null,
		skipped: false,
		skippedAt: null,
		link: '/air/automation-settings',
		docs: 'https://bitlyft.atlassian.net/wiki/spaces/BAHD/pages/1219133455/Mapping+a+Security+Policy+to+a+Automated+Remediation+Action+Playbook',
	},

	reviewNotifications: {
		order: 6,
		id: 'reviewNotifications',
		name: 'Review Notifications',
		description:
			'Explore the Notifications Center to review alerts, system updates, and actionable insights that keep you informed of critical events in your environment.',
		completed: false,
		required: false,
		roleRequired: 'Read Only',
		dependsOn: ['setupIntegration'],
		completedAt: null,
		skipped: false,
		skippedAt: null,
		link: '/settings/notifications',
		docs: 'https://bitlyft.atlassian.net/wiki/spaces/BAHD/pages/1051525125/Managing+Notifications+in+BitLyft+AIR',
	},

	inviteTeamMember: {
		order: 7,
		id: 'inviteTeamMember',
		name: 'Invite a Team Member',
		description:
			'Invite colleagues to collaborate in BitLyft AIR®, enabling shared visibility, task management, and efficient incident response across your team.',
		completed: false,
		required: false,
		roleRequired: 'System Manager',
		dependsOn: ['setupIntegration'],
		completedAt: null,
		skipped: false,
		skippedAt: null,
		link: '/settings/tenant-settings',
		docs: 'https://bitlyft.atlassian.net/wiki/spaces/BAHD/pages/900071439/How+to+Create+User',
	},
} as const
